import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { useAuth } from "./useAuth";
import {
  FaEnvelope, FaLock, FaGoogle, FaUser, FaPhone, FaTimes, FaEye, FaEyeSlash,
} from "react-icons/fa";
import "./UserSignup.css";
import Loader from "../components/loader";
import PasswordValidator from "./PasswordValidator";

function UserSignup({ onClose, onBackToLogin, embedded = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();

  const [formData, setFormData] = useState({ name: "", email: "", mobile: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [responseMsg, setResponseMsg] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [fullScreenLoading, setFullScreenLoading] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [consent, setConsent] = useState(false);

  const isDarkMode = document.body.classList.contains("dark-mode");
  const formRef = useRef(null);
  const scrollRef = useRef(null);

  const getPostLoginRedirect = () => {
    const storedRedirect =
      sessionStorage.getItem("postLoginRedirect") ||
      localStorage.getItem("postLoginRedirect");
    if (storedRedirect) return storedRedirect;

    const from = location.state?.from;
    if (typeof from === "string") return from;
    if (from?.pathname) {
      return `${from.pathname}${from.search || ""}${from.hash || ""}`;
    }

    return null;
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  useEffect(() => {
    const typedKey = "oauthSignupMessage_client";
    const fallbackKey = "oauthSignupMessage";
    const oauthMessage =
      sessionStorage.getItem(typedKey) || sessionStorage.getItem(fallbackKey);
    if (oauthMessage) {
      setResponseMsg(oauthMessage);
      sessionStorage.removeItem(typedKey);
      sessionStorage.removeItem(fallbackKey);
    }
  }, []);

  useEffect(() => {
    let interval = null;
    if (showVerifyModal && timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    } else if (timer === 0) {
      setCanResend(true);
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [showVerifyModal, timer]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      setResponseMsg("You must agree to the Privacy Policy and Terms & Conditions.");
      return;
    }
    setLoading(true);
    setFullScreenLoading(true);
    try {
      const userId = uuidv4().replace(/-/g, "").slice(0, 16);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, userId, consent, policyVersion: "v1.0", termsVersion: "v1.0" }),
      });
      const data = await response.json();

      if (response.ok) {
        // Backend now sends OTP to email
        setFullScreenLoading(false);
        setTimer(60);
        setCanResend(false);
        setOtpCode("");
        setOtpError("");
        setShowVerifyModal(true);
      } else {
        setResponseMsg(data.message || "Signup failed!");
        setFullScreenLoading(false);
      }
    } catch (error) {
      setResponseMsg("Something went wrong!");
      setFullScreenLoading(false);
    }
    setLoading(false);
  };

  const handleVerifyOtp = async () => {
    if (!otpCode.trim()) { setOtpError("Please enter the verification code."); return; }
    setOtpLoading(true);
    setOtpError("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/verify-signup-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email, otp: otpCode.trim() }),
        credentials: "include",
      });
      const data = await res.json();
      if (res.ok) {
        if (typeof refresh === "function") await refresh();
        localStorage.setItem("isLoggedIn", "true");
        window.dispatchEvent(new Event("auth-changed"));
        setShowVerifyModal(false);
        onClose && onClose();
        const pendingAction = localStorage.getItem("postAuthAction");
        const redirectTo = pendingAction || getPostLoginRedirect() || "/dashboard";
        navigate(redirectTo, { replace: true });
        localStorage.removeItem("postAuthAction");
        sessionStorage.removeItem("postLoginRedirect");
        localStorage.removeItem("postLoginRedirect");
      } else {
        setOtpError(data.message || "Invalid code. Please try again.");
      }
    } catch (err) {
      setOtpError("Something went wrong. Please try again.");
    }
    setOtpLoading(false);
  };

  const handleResend = async () => {
    if (!canResend) return;
    setTimer(60);
    setCanResend(false);
    setOtpError("");
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/resend-signup-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email }),
      });
    } catch (err) { /* silent */ }
  };

  const handleGoogleSignup = () => {
    if (!consent) return;
    const oauthUrl = `${process.env.REACT_APP_BACKEND_URL}/auth/google/signup`;
    const w = 520, h = 620;
    const left = Math.round(window.screenX + (window.outerWidth - w) / 2);
    const top = Math.round(window.screenY + (window.outerHeight - h) / 2);
    const popup = window.open(oauthUrl, "google-oauth", `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`);

    if (!popup) {
      setFullScreenLoading(true);
      window.location.href = oauthUrl;
      return;
    }

    setFullScreenLoading(true);

    const onMessage = async (event) => {
      if (event.data?.type !== "GOOGLE_AUTH_SUCCESS") return;
      window.removeEventListener("message", onMessage);
      clearInterval(pollClose);
      await refresh();
      localStorage.setItem("isLoggedIn", "true");
      window.dispatchEvent(new Event("auth-changed"));
      setFullScreenLoading(false);
      onClose && onClose();
      const pending = localStorage.getItem("postAuthAction");
      const redirectTo = pending || getPostLoginRedirect() || "/dashboard";
      navigate(redirectTo, { replace: true });
      localStorage.removeItem("postAuthAction");
      sessionStorage.removeItem("postLoginRedirect");
      localStorage.removeItem("postLoginRedirect");
    };
    window.addEventListener("message", onMessage);

    const pollClose = setInterval(() => {
      if (popup.closed) {
        clearInterval(pollClose);
        window.removeEventListener("message", onMessage);
        setFullScreenLoading(false);
      }
    }, 500);
  };

  // ✅ New Loader UI
  const renderLoaderLayer = () => {
    if (!fullScreenLoading) return null;
    return (
      <div className="fullscreen-loader-wrapper">
        <Loader />
      </div>
    );
  };

  const verifyModal = showVerifyModal ? (
    <div className="verify-modal-overlay-backdrop-x" onClick={(e) => { if (e.target === e.currentTarget) {} }}>
      <div className="verify-modal-content-x" onClick={(e) => e.stopPropagation()}>
        <button className="signup-modal-close" onClick={onClose} style={{ color: "white", position: "absolute", top: "16px", right: "18px" }}><FaTimes /></button>
        <img src="/image/email-verified.png" alt="Email Sent" className="verify-icon-img-y" />
        <h3 className="verify-title-z">Verify Your Email</h3>
        <p className="verify-text-a">
          We sent a 6-digit code to <span className="verify-email-highlight-b">{formData.email}</span>
        </p>
        <p className="verify-text-a" style={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.6)", marginBottom: "14px" }}>
          Enter the code below to complete sign-up.
        </p>
        <input
          type="text"
          inputMode="numeric"
          maxLength={6}
          placeholder="Enter 6-digit code"
          value={otpCode}
          onChange={(e) => { setOtpCode(e.target.value.replace(/\D/g, "")); setOtpError(""); }}
          className="verify-otp-input"
          style={{
            width: "80%", padding: "12px 16px", fontSize: "1.4rem",
            letterSpacing: "10px", textAlign: "center", borderRadius: "10px",
            border: "1.5px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.08)",
            color: "#fff", outline: "none", marginBottom: "10px",
          }}
        />
        {otpError && <p style={{ color: "#ff6b6b", fontSize: "0.85rem", marginBottom: "8px" }}>{otpError}</p>}
        <button
          onClick={handleVerifyOtp}
          disabled={otpLoading || otpCode.length < 6}
          className="verify-resend-btn-f enabled"
          style={{ width: "80%", padding: "11px", fontSize: "1rem", fontWeight: 700, background: "#F45900", border: "none", borderRadius: "10px", color: "#fff", cursor: "pointer", marginBottom: "14px" }}
        >
          {otpLoading ? "Verifying..." : "Verify Code"}
        </button>
        <div className="verify-footer-text-e">
          <button onClick={handleResend} disabled={!canResend} className={`verify-resend-btn-f ${canResend ? "enabled" : "disabled"}`}>
            {canResend ? "Resend Code" : `Resend Code (${timer}s)`}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  const leftContent = (
    <div className={`signup-left ${fullScreenLoading ? "content-blur" : ""}`} onWheel={(e) => { if (scrollRef.current) scrollRef.current.scrollTop += e.deltaY; }}>
      <div className="signup-card-wrapper" ref={formRef}>
        <div className="signup-header">
          <div className="auth-brand">
            <img src="/image/trans-logo.png" alt="MarsMate" className="auth-brand-icon" />
            <span className="auth-brand-text">MARSMATE AI</span>
          </div>
          <h2 className="signup-title-wrapper">Client Sign Up</h2>
        </div>
        <form onSubmit={handleSubmit} className="signup-form">
          <div className="input-group-signup"><FaUser className="input-icon-signup" /><input name="name" placeholder="Name" onChange={handleChange} required /></div>
          <div className="input-group-signup"><FaEnvelope className="input-icon-signup" /><input name="email" placeholder="Email" onChange={handleChange} required /></div>
          <div className="input-group-signup"><FaPhone className="input-icon-signup" /><input name="mobile" placeholder="Mobile Number" onChange={handleChange} required /></div>
          <div className="input-group-signup">
            <FaLock className="input-icon-signup" /><input type={showPassword ? "text" : "password"} name="password" placeholder="Password" onChange={handleChange} required />
            <span className="toggle-password-icon" onClick={() => setShowPassword(!showPassword)}>{showPassword ? <FaEye /> : <FaEyeSlash />}</span>
          </div>
          <div className="signup-consent-group">
            <input
              type="checkbox"
              id="signup-consent-client"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              required
            />
            <label htmlFor="signup-consent-client">
              I agree to the <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and <a href="/terms-conditions" target="_blank" rel="noopener noreferrer">Terms & Conditions</a>
            </label>
          </div>
          {responseMsg && <p className="response-msg">{responseMsg}</p>}
          <PasswordValidator password={formData.password} />
          <button className="btn-signup" disabled={loading || !consent}>{loading ? "Processing..." : "Sign Up"}</button>
          <div className="divider-login">or</div>
          <div className="social-signup-wrapper"><button type="button" className="social-btn-google-signup" onClick={handleGoogleSignup} disabled={loading || !consent}><FaGoogle /></button></div>
          <div className="signup-text-login">
            <span>Already have an account? </span>
            <span className="signup-clickable-text" onClick={(e) => { e.stopPropagation(); onBackToLogin(); }}>Login</span>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <>
      {renderLoaderLayer()}
      {embedded ? (
        <>
          {verifyModal}
          {leftContent}
        </>
      ) : (
        <div className="signup-modal-overlay" onClick={onClose}>
          {verifyModal}
          {!showVerifyModal && (
            <div className="modal-box" onClick={(e) => e.stopPropagation()}>
              <div className="signup-modal-split">
                {leftContent}
                <div ref={scrollRef} className="center-scroll-wrapper" onScroll={(e) => { if (formRef.current) formRef.current.style.transform = `translateY(-${e.target.scrollTop}px)`; }}>
                  <div className="center-scroll-content" />
                </div>
                <button className="signup-modal-close" onClick={onClose}><FaTimes /></button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default UserSignup;
